/**
 * Pairs category service copy with gallery images. Images use `pairingKey` from
 * the filename stem (see categoryAssets). "Why choose" promos are excluded from rows.
 */

function normalizeKey(s) {
  return s
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

/** Plural-friendly token match (e.g. seminar / seminars). */
function sameToken(a, b) {
  if (a === b) return true;
  if (a.length < 2 || b.length < 2) return false;
  if (a.includes(b) || b.includes(a)) return true;
  const [shorter, longer] = a.length <= b.length ? [a, b] : [b, a];
  if (longer.length === shorter.length + 1 && longer.startsWith(shorter)) {
    return longer.endsWith("s");
  }
  if (longer.endsWith("ies") && shorter.endsWith("y")) {
    return longer.slice(0, -3) + "y" === shorter;
  }
  return false;
}

function wordsMatch(serviceName, pairingKey) {
  const a = normalizeKey(serviceName);
  const b = normalizeKey(pairingKey);
  if (!a || !b) return false;
  if (a === b) return true;
  if (a.includes(b) || b.includes(a)) return true;
  const wa = a.split(/\s+/).filter(Boolean);
  const wb = b.split(/\s+/).filter(Boolean);
  if (wa.length === 0 || wb.length === 0) return false;
  const [shorter, longer] = wa.length <= wb.length ? [wa, wb] : [wb, wa];
  return shorter.every((w) => longer.some((lw) => sameToken(w, lw)));
}

function isWhyChooseImage(item) {
  const k = (item.pairingKey || "") + " " + (item.alt || "");
  return /why\s+choose/i.test(k);
}

/**
 * @param {Array<{ name: string, description: string }>} subEvents
 * @param {Array<{ src: string, alt: string, pairingKey?: string }>} gallery
 * @returns {Array<{ name: string, description: string, image: typeof gallery[0] | null }>}
 */
export function pairSubeventsWithGallery(subEvents, gallery) {
  if (!subEvents?.length) return [];
  const pool = (gallery || []).filter((g) => !isWhyChooseImage(g));
  const used = new Set();

  const findMatch = (name) => {
    const idx = pool.findIndex(
      (g, i) => !used.has(i) && wordsMatch(name, g.pairingKey || ""),
    );
    return idx;
  };

  return subEvents.map((se) => {
    let idx = findMatch(se.name);
    if (idx < 0) {
      idx = pool.findIndex((_, i) => !used.has(i));
    }
    if (idx < 0) {
      return { ...se, image: null };
    }
    used.add(idx);
    return { ...se, image: pool[idx] };
  });
}

/**
 * @param {Array<{ src: string, alt: string, pairingKey?: string }>} gallery
 * @returns {typeof gallery[0] | null}
 */
export function findWhyChooseImage(gallery) {
  if (!gallery?.length) return null;
  const found = gallery.find((g) => isWhyChooseImage(g));
  return found || null;
}
